import { useState } from 'react';
import { useUsers } from '@/lib/api/hooks';
import { PageHeader } from '@/components/layout/page-header';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Search, Shield } from 'lucide-react';
import { UserRolesModal } from '@/components/modals/UserRolesModal';
import { PermissionGuard } from '@/components/guards/PermissionGuard';
import { User } from '@/lib/api/types';

export default function UsersPage() {
    const [search, setSearch] = useState('');
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    const { data, isLoading } = useUsers({ query: search, limit: 50 });
    const users = data?.users || [];

    return (
        <div className="space-y-6">
            <PageHeader
                title="Users Management"
            />

            <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search users by name or email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                    />
                </div>
            </div>

            {isLoading ? (
                <div className="text-center py-12">Loading users...</div>
            ) : users.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center">
                        <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">No users found</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4">
                    {users.map((user) => (
                        <Card key={user.id} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        {user.imageUrl && (
                                            <img
                                                src={user.imageUrl}
                                                alt={user.fullName}
                                                className="h-10 w-10 rounded-full"
                                            />
                                        )}
                                        <div>
                                            <h3 className="font-semibold">{user.fullName || 'Unnamed User'}</h3>
                                            <p className="text-sm text-muted-foreground">{user.email}</p>
                                        </div>
                                    </div>
                                    <PermissionGuard resource="users" action="manage_roles">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setSelectedUser(user)}
                                        >
                                            <Shield className="h-4 w-4 mr-2" />
                                            Manage Roles
                                        </Button>
                                    </PermissionGuard>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {selectedUser && (
                <UserRolesModal
                    user={selectedUser}
                    open={!!selectedUser}
                    onClose={() => setSelectedUser(null)}
                />
            )}
        </div>
    );
}
